import { useTranslation } from 'react-i18next';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import Card from '@/components/base/Card';
import { formatNumber, formatTime } from '@/lib/format';
import type { LaundryModificationRequest, LaundryProfile } from '@/types';
import AddressMapCard from './AddressMapCard';
import ModificationStatusCard from './ModificationStatusCard';

interface LaundryDataSectionProps {
  profile: LaundryProfile;
  modification: LaundryModificationRequest | null;
  onRequestEdit: () => void;
}

function InfoItem({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 rounded-md border border-background-200 bg-background-100/60 px-3.5 py-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-50 text-foreground-500">
        <i className={`${icon} text-lg leading-none`} aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <p className="text-xs text-foreground-500">{label}</p>
        <p className="mt-0.5 text-sm font-semibold text-foreground-950">{value}</p>
      </div>
    </div>
  );
}

/** Approved laundry data + the latest change-request state. */
export default function LaundryDataSection({
  profile,
  modification,
  onRequestEdit,
}: LaundryDataSectionProps) {
  const { t, i18n } = useTranslation();

  const durationValue = (hours: number | null) =>
    hours == null ? t('settings.value.notSet') : `${formatNumber(hours)} ${t('settings.unit.hours')}`;

  return (
    <Card padded={false} className="overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-background-200 px-4 py-4 md:px-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-store-2-line text-xl leading-none" aria-hidden="true" />
          </span>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold text-foreground-950">
                {t('settings.section.laundry')}
              </h2>
              <Badge variant="accent" icon="ri-verified-badge-line">
                {t('settings.approved.badge')}
              </Badge>
            </div>
            <p className="mt-0.5 text-xs text-foreground-500">{t('settings.approvedNote')}</p>
          </div>
        </div>
        <Button variant="primary" size="md" icon="ri-edit-2-line" onClick={onRequestEdit}>
          {t('settings.requestEdit')}
        </Button>
      </div>

      <div className="space-y-5 p-4 md:p-5">
        {/* Identity */}
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-background-200 bg-background-100">
            {profile.logoUrl ? (
              <img src={profile.logoUrl} alt={profile.name} className="h-full w-full object-cover" />
            ) : (
              <i className="ri-image-2-line text-3xl text-foreground-400" aria-hidden="true" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-foreground-950">{profile.name}</h3>
            <p className="lb-ltr-field mt-1 flex items-center gap-1.5 text-sm text-foreground-600">
              <i className="ri-phone-line text-base text-foreground-400" aria-hidden="true" />
              {profile.phone}
            </p>
          </div>
        </div>

        {/* Description */}
        <div>
          <p className="text-xs text-foreground-500">{t('settings.field.description')}</p>
          <p className="mt-1 text-sm leading-relaxed text-foreground-800">{profile.description}</p>
        </div>

        {/* Hours + durations */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <InfoItem
            icon="ri-time-line"
            label={t('settings.field.workingHours')}
            value={`${formatTime(profile.fromTime, i18n.language)} - ${formatTime(profile.toTime, i18n.language)}`}
          />
          <InfoItem
            icon="ri-timer-line"
            label={t('settings.field.normalDuration')}
            value={durationValue(profile.normalHours)}
          />
          <InfoItem
            icon="ri-flashlight-line"
            label={t('settings.field.urgentDuration')}
            value={durationValue(profile.urgentHours)}
          />
        </div>

        {/* Address + map */}
        <div>
          <p className="mb-1.5 text-xs text-foreground-500">{t('settings.field.address')}</p>
          <AddressMapCard address={profile.address} title={profile.name} />
        </div>

        {/* Coverage */}
        <div>
          <p className="mb-1.5 text-xs text-foreground-500">{t('settings.field.coverage')}</p>
          {profile.coverageDistricts.length ? (
            <div className="flex flex-wrap gap-1.5">
              {profile.coverageDistricts.map((district) => (
                <Badge key={district} variant="secondary" icon="ri-map-pin-2-line">
                  {district}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-foreground-500">{t('settings.coverage.none')}</p>
          )}
        </div>

        <p className="flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
          <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
          {t('settings.duration.scope')}
        </p>
      </div>

      <div className="border-t border-background-100 bg-background-100/50 p-4 md:p-5">
        <ModificationStatusCard modification={modification} />
      </div>
    </Card>
  );
}